/**
 * Advisory taxonomy — client access to the platform's category/subcategory catalog.
 *
 * The SINGLE SOURCE OF TRUTH is the server (data/advisoryTaxonomy.ts), exposed via
 * `GET {REACT_APP_SERVER}onboarding/taxonomy`. The UI FETCHES it so we never drift
 * from the backend. FALLBACK_TAXONOMY below is only a small safety net used if that
 * request fails (e.g. transient network error) so the category picker is never blank.
 *
 * Storage contract the UI must honor when submitting:
 *   userCategories = the chosen category's `label`   (kept as-is for back-compat)
 *   subcategories  = array of chosen subcategory `slug`s
 */

// Safety-net copy of the server taxonomy (all 62 categories - one per platform AI agent family),
// used only if GET onboarding/taxonomy fails, so onboarding always offers the full list.
// Snapshot of Maven-platform-Server data/advisoryTaxonomy.ts taken 2026-10-22; the server stays
// authoritative. Refresh after changing the server taxonomy.
export const FALLBACK_TAXONOMY = [
  {
    "slug": "immigration",
    "label": "Immigration",
    "group": "Legal & Immigration",
    "subLabel": "Destination country",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "canada",
        "label": "Canada"
      },
      {
        "slug": "usa",
        "label": "United States"
      },
      {
        "slug": "israel",
        "label": "Israel"
      },
      {
        "slug": "uk",
        "label": "United Kingdom"
      },
      {
        "slug": "australia",
        "label": "Australia"
      },
      {
        "slug": "new-zealand",
        "label": "New Zealand"
      },
      {
        "slug": "ireland",
        "label": "Ireland"
      },
      {
        "slug": "germany",
        "label": "Germany"
      },
      {
        "slug": "france",
        "label": "France"
      },
      {
        "slug": "netherlands",
        "label": "Netherlands"
      },
      {
        "slug": "portugal",
        "label": "Portugal"
      },
      {
        "slug": "spain",
        "label": "Spain"
      },
      {
        "slug": "schengen-eu",
        "label": "Schengen / EU"
      },
      {
        "slug": "uae",
        "label": "United Arab Emirates"
      },
      {
        "slug": "singapore",
        "label": "Singapore"
      },
      {
        "slug": "other-country",
        "label": "Other country"
      }
    ]
  },
  {
    "slug": "legal",
    "label": "Legal",
    "group": "Legal & Immigration",
    "subLabel": "Practice area",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "immigration-law",
        "label": "Immigration law"
      },
      {
        "slug": "family-law",
        "label": "Family law"
      },
      {
        "slug": "criminal-defense",
        "label": "Criminal defense"
      },
      {
        "slug": "real-estate-law",
        "label": "Real estate law"
      },
      {
        "slug": "corporate-business-law",
        "label": "Corporate & business law"
      },
      {
        "slug": "employment-law",
        "label": "Employment & labor law"
      },
      {
        "slug": "intellectual-property",
        "label": "Intellectual property"
      },
      {
        "slug": "personal-injury",
        "label": "Personal injury"
      },
      {
        "slug": "estate-wills",
        "label": "Estate planning & wills"
      },
      {
        "slug": "tax-law",
        "label": "Tax law"
      },
      {
        "slug": "bankruptcy-debt",
        "label": "Bankruptcy & debt"
      },
      {
        "slug": "civil-litigation",
        "label": "Civil litigation"
      },
      {
        "slug": "contract-law",
        "label": "Contract law"
      }
    ]
  },
  {
    "slug": "financial",
    "label": "Financial & Investment",
    "group": "Finance & Accounting",
    "subLabel": "Specialization",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "financial-planning",
        "label": "Financial planning"
      },
      {
        "slug": "wealth-investment",
        "label": "Wealth & investment management"
      },
      {
        "slug": "stock-market-trading",
        "label": "Stock market & trading"
      },
      {
        "slug": "retirement-planning",
        "label": "Retirement planning"
      },
      {
        "slug": "insurance-advisory",
        "label": "Insurance advisory"
      },
      {
        "slug": "debt-credit",
        "label": "Debt & credit counseling"
      },
      {
        "slug": "mortgage-lending",
        "label": "Mortgage & lending"
      },
      {
        "slug": "estate-inheritance",
        "label": "Estate & inheritance planning"
      },
      {
        "slug": "crypto-digital-assets",
        "label": "Cryptocurrency & digital assets"
      },
      {
        "slug": "business-corporate-finance",
        "label": "Business & corporate finance"
      },
      {
        "slug": "risk-management",
        "label": "Risk management"
      }
    ]
  },
  {
    "slug": "accounting-tax",
    "label": "Accounting & Tax",
    "group": "Finance & Accounting",
    "subLabel": "Specialization",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "personal-tax",
        "label": "Personal tax"
      },
      {
        "slug": "corporate-tax",
        "label": "Corporate tax"
      },
      {
        "slug": "bookkeeping",
        "label": "Bookkeeping"
      },
      {
        "slug": "payroll",
        "label": "Payroll"
      },
      {
        "slug": "audit-assurance",
        "label": "Audit & assurance"
      },
      {
        "slug": "international-tax",
        "label": "International tax"
      },
      {
        "slug": "sales-vat-tax",
        "label": "Sales / VAT tax"
      },
      {
        "slug": "cfo-advisory",
        "label": "CFO / financial advisory"
      }
    ]
  },
  {
    "slug": "insurance",
    "label": "Insurance",
    "group": "Finance & Accounting",
    "subLabel": "Line of business",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "life-insurance",
        "label": "Life"
      },
      {
        "slug": "health-insurance",
        "label": "Health"
      },
      {
        "slug": "auto-insurance",
        "label": "Auto"
      },
      {
        "slug": "home-property-insurance",
        "label": "Home / Property"
      },
      {
        "slug": "business-commercial-insurance",
        "label": "Business / Commercial"
      },
      {
        "slug": "travel-insurance",
        "label": "Travel"
      },
      {
        "slug": "disability-insurance",
        "label": "Disability"
      },
      {
        "slug": "pension-annuity",
        "label": "Pension / Annuity"
      }
    ]
  },
  {
    "slug": "real-estate",
    "label": "Real Estate & Property",
    "group": "Property & Real Estate",
    "subLabel": "Specialization",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "residential-sales",
        "label": "Residential sales"
      },
      {
        "slug": "commercial-real-estate",
        "label": "Commercial real estate"
      },
      {
        "slug": "property-management",
        "label": "Property management"
      },
      {
        "slug": "real-estate-investment",
        "label": "Real estate investment"
      },
      {
        "slug": "mortgage-brokerage",
        "label": "Mortgage brokerage"
      },
      {
        "slug": "interior-design",
        "label": "Interior design"
      },
      {
        "slug": "home-improvement",
        "label": "Home improvement"
      },
      {
        "slug": "relocation",
        "label": "Relocation services"
      }
    ]
  },
  {
    "slug": "business-consulting",
    "label": "Business & Management Consulting",
    "group": "Business & Career",
    "subLabel": "Focus area",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "strategy",
        "label": "Strategy"
      },
      {
        "slug": "startup-entrepreneurship",
        "label": "Startup & entrepreneurship"
      },
      {
        "slug": "operations",
        "label": "Operations"
      },
      {
        "slug": "marketing-growth",
        "label": "Marketing & growth"
      },
      {
        "slug": "sales",
        "label": "Sales"
      },
      {
        "slug": "hr-organization",
        "label": "HR & organization"
      },
      {
        "slug": "fundraising-finance",
        "label": "Fundraising & finance"
      },
      {
        "slug": "franchise",
        "label": "Franchise"
      },
      {
        "slug": "mergers-acquisitions",
        "label": "Mergers & acquisitions"
      },
      {
        "slug": "nonprofit",
        "label": "Nonprofit"
      },
      {
        "slug": "sustainability-esg",
        "label": "Sustainability / ESG"
      }
    ]
  },
  {
    "slug": "career-employment",
    "label": "Career & Employment",
    "group": "Business & Career",
    "subLabel": "Service",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "career-coaching",
        "label": "Career coaching"
      },
      {
        "slug": "resume-linkedin",
        "label": "Resume & LinkedIn"
      },
      {
        "slug": "executive-recruiting",
        "label": "Executive recruiting"
      },
      {
        "slug": "hr-consulting",
        "label": "HR consulting"
      },
      {
        "slug": "interview-coaching",
        "label": "Interview coaching"
      },
      {
        "slug": "job-placement",
        "label": "Job placement"
      },
      {
        "slug": "staffing-labour",
        "label": "Staffing & labour hire"
      },
      {
        "slug": "leadership-development",
        "label": "Leadership development"
      }
    ]
  },
  {
    "slug": "health-wellness",
    "label": "Health & Wellness",
    "group": "Health & Wellness",
    "subLabel": "Specialization",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "nutrition-dietetics",
        "label": "Nutrition & dietetics"
      },
      {
        "slug": "fitness-training",
        "label": "Fitness & personal training"
      },
      {
        "slug": "mental-health-therapy",
        "label": "Mental health & therapy"
      },
      {
        "slug": "medical-consulting",
        "label": "Medical consulting"
      },
      {
        "slug": "addiction-counseling",
        "label": "Addiction counseling"
      },
      {
        "slug": "physiotherapy",
        "label": "Physiotherapy & rehab"
      },
      {
        "slug": "holistic-alternative",
        "label": "Holistic & alternative medicine"
      },
      {
        "slug": "sleep-wellness",
        "label": "Sleep & recovery"
      }
    ]
  },
  {
    "slug": "coaching-personal-development",
    "label": "Coaching & Personal Development",
    "group": "Health & Wellness",
    "subLabel": "Focus",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "life-coaching",
        "label": "Life coaching"
      },
      {
        "slug": "executive-coaching",
        "label": "Executive & business coaching"
      },
      {
        "slug": "relationship-coaching",
        "label": "Relationship coaching"
      },
      {
        "slug": "mindset-performance",
        "label": "Mindset & performance"
      },
      {
        "slug": "spiritual-guidance",
        "label": "Spiritual guidance"
      },
      {
        "slug": "parenting",
        "label": "Parenting"
      },
      {
        "slug": "productivity",
        "label": "Productivity"
      },
      {
        "slug": "public-speaking",
        "label": "Public speaking"
      }
    ]
  },
  {
    "slug": "education-academic",
    "label": "Education & Academic",
    "group": "Education & Coaching",
    "subLabel": "Service",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "tutoring",
        "label": "Tutoring"
      },
      {
        "slug": "test-prep",
        "label": "Test preparation"
      },
      {
        "slug": "admissions-consulting",
        "label": "University / college admissions"
      },
      {
        "slug": "study-abroad",
        "label": "Study abroad"
      },
      {
        "slug": "special-education",
        "label": "Special education"
      },
      {
        "slug": "language-learning",
        "label": "Language learning"
      },
      {
        "slug": "vocational-training",
        "label": "Career / vocational training"
      }
    ]
  },
  {
    "slug": "technology-digital",
    "label": "Technology & Digital",
    "group": "Technology & Marketing",
    "subLabel": "Specialization",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "it-consulting",
        "label": "IT consulting"
      },
      {
        "slug": "software-development",
        "label": "Software development"
      },
      {
        "slug": "web-development",
        "label": "Web development"
      },
      {
        "slug": "cybersecurity",
        "label": "Cybersecurity"
      },
      {
        "slug": "cloud-devops",
        "label": "Cloud & DevOps"
      },
      {
        "slug": "data-ai",
        "label": "Data & AI"
      },
      {
        "slug": "product-management",
        "label": "Product management"
      },
      {
        "slug": "ux-ui-design",
        "label": "UX / UI design"
      }
    ]
  },
  {
    "slug": "marketing-creative",
    "label": "Marketing & Creative",
    "group": "Technology & Marketing",
    "subLabel": "Specialization",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "digital-marketing",
        "label": "Digital marketing"
      },
      {
        "slug": "seo-sem",
        "label": "SEO / SEM"
      },
      {
        "slug": "social-media",
        "label": "Social media"
      },
      {
        "slug": "branding",
        "label": "Branding"
      },
      {
        "slug": "content-marketing",
        "label": "Content marketing"
      },
      {
        "slug": "graphic-design",
        "label": "Graphic design"
      },
      {
        "slug": "public-relations",
        "label": "Public relations"
      },
      {
        "slug": "photography-video",
        "label": "Photography & video"
      }
    ]
  },
  {
    "slug": "travel-hospitality",
    "label": "Travel & Hospitality",
    "group": "Lifestyle & Services",
    "subLabel": "Specialization",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "travel-planning",
        "label": "Travel planning"
      },
      {
        "slug": "corporate-travel",
        "label": "Corporate travel"
      },
      {
        "slug": "luxury-travel",
        "label": "Luxury travel"
      },
      {
        "slug": "event-planning",
        "label": "Event planning"
      },
      {
        "slug": "tour-operations",
        "label": "Tour operations"
      },
      {
        "slug": "destination-relocation",
        "label": "Relocation"
      }
    ]
  },
  {
    "slug": "lifestyle-personal-services",
    "label": "Lifestyle & Personal Services",
    "group": "Lifestyle & Services",
    "subLabel": "Specialization",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "personal-styling",
        "label": "Personal styling"
      },
      {
        "slug": "automotive",
        "label": "Automotive"
      },
      {
        "slug": "pet-care",
        "label": "Pet care"
      },
      {
        "slug": "senior-care",
        "label": "Senior care"
      },
      {
        "slug": "home-organization",
        "label": "Home organization"
      },
      {
        "slug": "personal-shopping",
        "label": "Personal shopping"
      },
      {
        "slug": "wedding-planning",
        "label": "Wedding planning"
      }
    ]
  },
  {
    "slug": "environmental-sustainability",
    "label": "Environmental & Sustainability",
    "group": "Specialized & Other",
    "subLabel": "Specialization",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "environmental-consulting",
        "label": "Environmental consulting"
      },
      {
        "slug": "energy-solar",
        "label": "Energy & solar"
      },
      {
        "slug": "esg-compliance",
        "label": "ESG & compliance"
      },
      {
        "slug": "waste-management",
        "label": "Waste management"
      }
    ]
  },
  {
    "slug": "family-relationships",
    "label": "Family & Relationships",
    "group": "Family & Home",
    "subLabel": "Area",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "couples-marriage",
        "label": "Couples & marriage"
      },
      {
        "slug": "separation-divorce-support",
        "label": "Separation & divorce support"
      },
      {
        "slug": "co-parenting",
        "label": "Co-parenting"
      },
      {
        "slug": "parenting-child-development",
        "label": "Parenting & child development"
      },
      {
        "slug": "teens-young-adults",
        "label": "Teens & young adults"
      },
      {
        "slug": "blended-families",
        "label": "Blended families"
      },
      {
        "slug": "family-conflict",
        "label": "Family conflict & communication"
      },
      {
        "slug": "caring-for-aging-parents",
        "label": "Caring for aging parents"
      }
    ]
  },
  {
    "slug": "pets-veterinary",
    "label": "Pets & Veterinary",
    "group": "Family & Home",
    "subLabel": "Area",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "dog-health",
        "label": "Dog health"
      },
      {
        "slug": "cat-health",
        "label": "Cat health"
      },
      {
        "slug": "exotic-small-pets",
        "label": "Exotic & small pets"
      },
      {
        "slug": "behaviour-training",
        "label": "Behaviour & training"
      },
      {
        "slug": "pet-nutrition",
        "label": "Pet nutrition"
      },
      {
        "slug": "new-pet-adoption",
        "label": "New pet & adoption"
      },
      {
        "slug": "pet-costs-insurance",
        "label": "Vet costs & pet insurance"
      },
      {
        "slug": "senior-pets-end-of-life",
        "label": "Senior pets & end of life"
      }
    ]
  },
  {
    "slug": "mental-health",
    "label": "Mental Health & Wellbeing",
    "group": "Health & Wellness",
    "subLabel": "Area",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "anxiety-stress",
        "label": "Anxiety & stress"
      },
      {
        "slug": "low-mood-depression",
        "label": "Low mood & depression"
      },
      {
        "slug": "burnout-work-stress",
        "label": "Burnout & work stress"
      },
      {
        "slug": "grief-loss",
        "label": "Grief & loss"
      },
      {
        "slug": "trauma-support",
        "label": "Trauma support"
      },
      {
        "slug": "finding-therapy",
        "label": "Finding the right therapy"
      },
      {
        "slug": "youth-mental-health",
        "label": "Child & teen mental health"
      },
      {
        "slug": "supporting-someone",
        "label": "Supporting someone else"
      }
    ]
  },
  {
    "slug": "home-renovation",
    "label": "Home Renovation & Repair",
    "group": "Property & Real Estate",
    "subLabel": "Project type",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "renovation-planning",
        "label": "Renovation planning & budget"
      },
      {
        "slug": "kitchen-bathroom",
        "label": "Kitchen & bathroom"
      },
      {
        "slug": "contractors-quotes",
        "label": "Choosing contractors & quotes"
      },
      {
        "slug": "permits-codes",
        "label": "Permits & building codes"
      },
      {
        "slug": "repairs-maintenance",
        "label": "Repairs & maintenance"
      },
      {
        "slug": "energy-retrofit",
        "label": "Energy efficiency retrofit"
      },
      {
        "slug": "water-damp-mould",
        "label": "Water, damp & mould"
      },
      {
        "slug": "accessibility-modifications",
        "label": "Accessibility modifications"
      }
    ]
  },
  {
    "slug": "housing-tenancy",
    "label": "Renting & Tenancy",
    "group": "Property & Real Estate",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "finding-rental",
        "label": "Finding a rental"
      },
      {
        "slug": "leases-agreements",
        "label": "Leases & agreements"
      },
      {
        "slug": "deposits",
        "label": "Deposits"
      },
      {
        "slug": "rent-increases",
        "label": "Rent increases"
      },
      {
        "slug": "repairs-habitability",
        "label": "Repairs & habitability"
      },
      {
        "slug": "eviction-notices",
        "label": "Eviction & notices"
      },
      {
        "slug": "roommates-subletting",
        "label": "Roommates & subletting"
      },
      {
        "slug": "small-landlords",
        "label": "Small landlords"
      }
    ]
  },
  {
    "slug": "hr-employers",
    "label": "HR & Employer Advice",
    "group": "Business & Career",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "hiring-contracts",
        "label": "Hiring & employment contracts"
      },
      {
        "slug": "payroll-compliance",
        "label": "Payroll & employer compliance"
      },
      {
        "slug": "workplace-policies",
        "label": "Workplace policies & handbooks"
      },
      {
        "slug": "performance-discipline",
        "label": "Performance & discipline"
      },
      {
        "slug": "terminations-layoffs",
        "label": "Terminations & layoffs"
      },
      {
        "slug": "workplace-disputes",
        "label": "Workplace disputes & investigations"
      },
      {
        "slug": "remote-international-hiring",
        "label": "Remote & international hiring"
      },
      {
        "slug": "compensation-benefits",
        "label": "Compensation & benefits design"
      }
    ]
  },
  {
    "slug": "automotive",
    "label": "Automotive & Vehicles",
    "group": "Lifestyle & Services",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "buying-car",
        "label": "Buying a car (new or used)"
      },
      {
        "slug": "lease-vs-finance",
        "label": "Leasing vs financing"
      },
      {
        "slug": "electric-hybrid",
        "label": "Electric & hybrid vehicles"
      },
      {
        "slug": "repairs-diagnostics",
        "label": "Repairs & diagnostics"
      },
      {
        "slug": "warranty-recalls",
        "label": "Warranty & recalls"
      },
      {
        "slug": "accidents-claims",
        "label": "Accidents & insurance claims"
      },
      {
        "slug": "selling-trade-in",
        "label": "Selling & trade-in"
      },
      {
        "slug": "business-fleet",
        "label": "Business & fleet vehicles"
      }
    ]
  },
  {
    "slug": "tech-support",
    "label": "Tech Support & Devices",
    "group": "Technology & Marketing",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "computers-laptops",
        "label": "Computers & laptops"
      },
      {
        "slug": "phones-tablets",
        "label": "Phones & tablets"
      },
      {
        "slug": "home-wifi-networking",
        "label": "Home Wi-Fi & networking"
      },
      {
        "slug": "smart-home",
        "label": "Smart home"
      },
      {
        "slug": "backup-data-recovery",
        "label": "Backup & data recovery"
      },
      {
        "slug": "hacked-accounts-scams",
        "label": "Hacked accounts & online scams"
      },
      {
        "slug": "buying-devices",
        "label": "Choosing a device"
      },
      {
        "slug": "software-apps",
        "label": "Software & apps"
      }
    ]
  },
  {
    "slug": "benefits-social-rights",
    "label": "Government Benefits & Social Rights",
    "group": "Rights & Benefits",
    "subLabel": "Benefit area",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "disability-benefits",
        "label": "Disability benefits"
      },
      {
        "slug": "unemployment-benefits",
        "label": "Unemployment benefits"
      },
      {
        "slug": "old-age-pensions",
        "label": "Old-age & public pensions"
      },
      {
        "slug": "family-child-benefits",
        "label": "Family & child benefits"
      },
      {
        "slug": "housing-assistance",
        "label": "Housing assistance"
      },
      {
        "slug": "public-health-coverage",
        "label": "Public health coverage"
      },
      {
        "slug": "low-income-support",
        "label": "Low-income support"
      },
      {
        "slug": "appeals-reviews",
        "label": "Appeals & reviews"
      }
    ]
  },
  {
    "slug": "consumer-rights",
    "label": "Consumer Rights & Disputes",
    "group": "Rights & Benefits",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "refunds-returns",
        "label": "Refunds & returns"
      },
      {
        "slug": "faulty-goods-warranties",
        "label": "Faulty goods & warranties"
      },
      {
        "slug": "online-shopping-delivery",
        "label": "Online shopping & delivery"
      },
      {
        "slug": "scams-fraud",
        "label": "Scams & fraud"
      },
      {
        "slug": "subscriptions-contracts",
        "label": "Subscriptions & contracts"
      },
      {
        "slug": "bank-telecom-complaints",
        "label": "Bank, telecom & utility complaints"
      },
      {
        "slug": "travel-disruption",
        "label": "Flight & travel disruption"
      },
      {
        "slug": "small-claims-prep",
        "label": "Preparing a small claim"
      }
    ]
  },
  {
    "slug": "nutrition-fitness",
    "label": "Nutrition, Diet & Fitness",
    "group": "Health & Wellness",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "healthy-eating",
        "label": "Healthy eating & meal planning"
      },
      {
        "slug": "weight-management",
        "label": "Weight management"
      },
      {
        "slug": "sports-nutrition",
        "label": "Sports nutrition & performance"
      },
      {
        "slug": "exercise-programs",
        "label": "Exercise programs & training"
      },
      {
        "slug": "diet-conditions",
        "label": "Diet for health conditions"
      },
      {
        "slug": "food-allergies",
        "label": "Food allergies & intolerances"
      },
      {
        "slug": "supplements",
        "label": "Supplements & vitamins"
      },
      {
        "slug": "family-child-nutrition",
        "label": "Family & child nutrition"
      }
    ]
  },
  {
    "slug": "addiction-recovery",
    "label": "Addiction & Recovery",
    "group": "Health & Wellness",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "alcohol",
        "label": "Alcohol"
      },
      {
        "slug": "drugs-substances",
        "label": "Drugs & substances"
      },
      {
        "slug": "prescription-misuse",
        "label": "Prescription medication misuse"
      },
      {
        "slug": "gambling",
        "label": "Gambling"
      },
      {
        "slug": "smoking-vaping",
        "label": "Smoking & vaping"
      },
      {
        "slug": "digital-gaming",
        "label": "Gaming & screen overuse"
      },
      {
        "slug": "treatment-options",
        "label": "Finding treatment & support"
      },
      {
        "slug": "family-of-person",
        "label": "Supporting a family member"
      }
    ]
  },
  {
    "slug": "eldercare-aging",
    "label": "Eldercare & Aging",
    "group": "Family & Home",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "home-care",
        "label": "Home care & help at home"
      },
      {
        "slug": "care-homes",
        "label": "Assisted living & care homes"
      },
      {
        "slug": "dementia-memory",
        "label": "Dementia & memory loss"
      },
      {
        "slug": "caregiver-support",
        "label": "Support for family caregivers"
      },
      {
        "slug": "elder-finances",
        "label": "Paying for care"
      },
      {
        "slug": "aging-in-place",
        "label": "Aging in place & home safety"
      },
      {
        "slug": "healthcare-navigation",
        "label": "Health care navigation for seniors"
      },
      {
        "slug": "elder-abuse-safety",
        "label": "Elder abuse & scams"
      }
    ]
  },
  {
    "slug": "pregnancy-parenthood",
    "label": "Pregnancy, Fertility & New Parents",
    "group": "Family & Home",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "trying-to-conceive",
        "label": "Trying to conceive"
      },
      {
        "slug": "fertility-treatment",
        "label": "Fertility treatment & IVF"
      },
      {
        "slug": "pregnancy-care",
        "label": "Pregnancy & prenatal care"
      },
      {
        "slug": "birth-planning",
        "label": "Birth planning"
      },
      {
        "slug": "postpartum",
        "label": "Postpartum recovery"
      },
      {
        "slug": "newborn-care",
        "label": "Newborn & baby care"
      },
      {
        "slug": "parental-leave",
        "label": "Parental leave & benefits"
      },
      {
        "slug": "adoption-surrogacy",
        "label": "Adoption & surrogacy"
      }
    ]
  },
  {
    "slug": "end-of-life",
    "label": "End-of-Life, Funeral & Bereavement",
    "group": "Family & Home",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "advance-planning",
        "label": "Advance care planning"
      },
      {
        "slug": "palliative-hospice",
        "label": "Palliative & hospice care"
      },
      {
        "slug": "funeral-planning",
        "label": "Funeral & burial planning"
      },
      {
        "slug": "after-a-death",
        "label": "What to do after a death"
      },
      {
        "slug": "estate-admin-basics",
        "label": "Settling affairs & paperwork"
      },
      {
        "slug": "death-benefits",
        "label": "Survivor & death benefits"
      },
      {
        "slug": "grief-support",
        "label": "Grief support"
      },
      {
        "slug": "digital-legacy",
        "label": "Digital legacy & accounts"
      }
    ]
  },
  {
    "slug": "disability-accessibility",
    "label": "Disability & Accessibility",
    "group": "Rights & Benefits",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "new-diagnosis",
        "label": "Living with a new disability"
      },
      {
        "slug": "assistive-technology",
        "label": "Assistive technology & equipment"
      },
      {
        "slug": "workplace-accommodation",
        "label": "Workplace accommodations"
      },
      {
        "slug": "education-accommodation",
        "label": "School & education accommodations"
      },
      {
        "slug": "accessible-housing",
        "label": "Accessible housing & home modifications"
      },
      {
        "slug": "disability-services",
        "label": "Disability services & funding"
      },
      {
        "slug": "child-disability",
        "label": "Children with disabilities"
      },
      {
        "slug": "accessibility-rights",
        "label": "Accessibility rights & complaints"
      }
    ]
  },
  {
    "slug": "veterans-military",
    "label": "Veterans & Military Families",
    "group": "Rights & Benefits",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "transition",
        "label": "Transition to civilian life"
      },
      {
        "slug": "disability-compensation",
        "label": "Service-related disability compensation"
      },
      {
        "slug": "veteran-health",
        "label": "Health care & mental health"
      },
      {
        "slug": "education-training",
        "label": "Education & training benefits"
      },
      {
        "slug": "employment",
        "label": "Jobs & careers after service"
      },
      {
        "slug": "housing-loans",
        "label": "Housing & home loans"
      },
      {
        "slug": "families-survivors",
        "label": "Military families & survivors"
      },
      {
        "slug": "reservists",
        "label": "Reserve duty & reservists"
      }
    ]
  },
  {
    "slug": "freelance-self-employment",
    "label": "Freelancing & Self-Employment",
    "group": "Business & Career",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "getting-started",
        "label": "Going freelance"
      },
      {
        "slug": "business-registration",
        "label": "Registering your business"
      },
      {
        "slug": "pricing-rates",
        "label": "Pricing & rates"
      },
      {
        "slug": "finding-clients",
        "label": "Finding clients"
      },
      {
        "slug": "contracts-invoicing",
        "label": "Contracts, invoicing & late payments"
      },
      {
        "slug": "taxes-self-employed",
        "label": "Taxes for the self-employed"
      },
      {
        "slug": "benefits-insurance",
        "label": "Insurance, pension & benefits"
      },
      {
        "slug": "gig-platforms",
        "label": "Gig & platform work"
      }
    ]
  },
  {
    "slug": "ecommerce-online-selling",
    "label": "E-commerce & Online Selling",
    "group": "Business & Career",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "starting-store",
        "label": "Starting an online store"
      },
      {
        "slug": "marketplaces",
        "label": "Selling on marketplaces"
      },
      {
        "slug": "product-sourcing",
        "label": "Product sourcing & suppliers"
      },
      {
        "slug": "payments-fraud",
        "label": "Payments & fraud"
      },
      {
        "slug": "shipping-fulfilment",
        "label": "Shipping & fulfilment"
      },
      {
        "slug": "sales-tax-ecommerce",
        "label": "Sales tax & VAT for online sales"
      },
      {
        "slug": "store-policies",
        "label": "Returns, policies & compliance"
      },
      {
        "slug": "growth-marketing",
        "label": "Growing online sales"
      }
    ]
  },
  {
    "slug": "moving-relocation",
    "label": "Moving & Relocation",
    "group": "Lifestyle & Services",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "planning-a-move",
        "label": "Planning a move"
      },
      {
        "slug": "hiring-movers",
        "label": "Hiring movers & quotes"
      },
      {
        "slug": "moving-costs",
        "label": "Moving costs & budget"
      },
      {
        "slug": "international-move",
        "label": "International moves & shipping"
      },
      {
        "slug": "moving-with-family",
        "label": "Moving with kids & pets"
      },
      {
        "slug": "address-utilities",
        "label": "Address changes & utilities"
      },
      {
        "slug": "moving-for-work",
        "label": "Moving for work"
      },
      {
        "slug": "settling-in",
        "label": "Settling into a new city"
      }
    ]
  },
  {
    "slug": "newcomer-settlement",
    "label": "Newcomer Settlement",
    "group": "Legal & Immigration",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "first-weeks",
        "label": "First weeks after arrival"
      },
      {
        "slug": "id-numbers",
        "label": "Government ID & numbers"
      },
      {
        "slug": "banking-credit",
        "label": "Banking & building credit"
      },
      {
        "slug": "health-coverage",
        "label": "Getting health coverage"
      },
      {
        "slug": "first-home",
        "label": "Finding a first home"
      },
      {
        "slug": "credential-recognition",
        "label": "Foreign credentials & licences"
      },
      {
        "slug": "schools-children",
        "label": "Schools & childcare for newcomers"
      },
      {
        "slug": "language-community",
        "label": "Language classes & community"
      }
    ]
  },
  {
    "slug": "workplace-rights",
    "label": "Employee Rights at Work",
    "group": "Business & Career",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "unpaid-wages",
        "label": "Unpaid wages"
      },
      {
        "slug": "hours-overtime",
        "label": "Hours & overtime"
      },
      {
        "slug": "dismissal-severance",
        "label": "Dismissal & severance"
      },
      {
        "slug": "discrimination-harassment",
        "label": "Discrimination & harassment"
      },
      {
        "slug": "workplace-injury",
        "label": "Workplace injury"
      },
      {
        "slug": "leave-rights",
        "label": "Leave rights"
      },
      {
        "slug": "contracts-noncompete",
        "label": "Contracts & non-competes"
      },
      {
        "slug": "retaliation-whistleblowing",
        "label": "Retaliation & whistleblowing"
      }
    ]
  },
  {
    "slug": "healthcare-access",
    "label": "Healthcare Navigation & Medical Bills",
    "group": "Health & Wellness",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "finding-care",
        "label": "Finding the right care"
      },
      {
        "slug": "wait-times",
        "label": "Specialists & wait times"
      },
      {
        "slug": "insurance-coverage",
        "label": "Health coverage & plans"
      },
      {
        "slug": "claim-denials",
        "label": "Denied claims & appeals"
      },
      {
        "slug": "medical-bills",
        "label": "Medical bills"
      },
      {
        "slug": "prescription-costs",
        "label": "Prescription costs"
      },
      {
        "slug": "second-opinion",
        "label": "Getting a second opinion"
      },
      {
        "slug": "patient-rights",
        "label": "Patient rights & records"
      }
    ]
  },
  {
    "slug": "personal-budgeting",
    "label": "Budgeting & Everyday Money",
    "group": "Finance & Accounting",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "monthly-budget",
        "label": "Monthly budget"
      },
      {
        "slug": "emergency-fund",
        "label": "Emergency fund"
      },
      {
        "slug": "cutting-costs",
        "label": "Cutting costs"
      },
      {
        "slug": "debt-payoff",
        "label": "Paying off debt"
      },
      {
        "slug": "credit-score",
        "label": "Credit score"
      },
      {
        "slug": "banking-basics",
        "label": "Banking basics"
      },
      {
        "slug": "couples-money",
        "label": "Money as a couple"
      },
      {
        "slug": "saving-goals",
        "label": "Saving for goals"
      }
    ]
  },
  {
    "slug": "childcare-early-years",
    "label": "Childcare & Early Years",
    "group": "Family & Home",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "finding-daycare",
        "label": "Finding daycare"
      },
      {
        "slug": "childcare-costs",
        "label": "Childcare costs & subsidies"
      },
      {
        "slug": "nannies-babysitters",
        "label": "Nannies & babysitters"
      },
      {
        "slug": "preschool",
        "label": "Preschool & kindergarten readiness"
      },
      {
        "slug": "early-development",
        "label": "Early development & milestones"
      },
      {
        "slug": "sleep-routines",
        "label": "Sleep & daily routines"
      },
      {
        "slug": "starting-school",
        "label": "Starting school"
      },
      {
        "slug": "working-parents",
        "label": "Balancing work and childcare"
      }
    ]
  },
  {
    "slug": "womens-health-menopause",
    "label": "Women's Health & Menopause",
    "group": "Health & Wellness",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "menopause",
        "label": "Menopause & perimenopause"
      },
      {
        "slug": "menstrual-health",
        "label": "Periods & menstrual health"
      },
      {
        "slug": "contraception",
        "label": "Contraception"
      },
      {
        "slug": "screenings",
        "label": "Screenings & check-ups"
      },
      {
        "slug": "pelvic-health",
        "label": "Pelvic health"
      },
      {
        "slug": "hormonal-conditions",
        "label": "PCOS, endometriosis & hormonal conditions"
      },
      {
        "slug": "bone-heart-health",
        "label": "Bone & heart health"
      },
      {
        "slug": "sexual-health",
        "label": "Sexual health"
      }
    ]
  },
  {
    "slug": "condo-hoa-neighbours",
    "label": "Condo, HOA & Neighbour Disputes",
    "group": "Property & Real Estate",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "noise-nuisance",
        "label": "Noise & nuisance"
      },
      {
        "slug": "boundaries-trees",
        "label": "Boundaries, fences & trees"
      },
      {
        "slug": "board-disputes",
        "label": "Condo & HOA board disputes"
      },
      {
        "slug": "fees-assessments",
        "label": "Fees & special assessments"
      },
      {
        "slug": "common-repairs",
        "label": "Common-area repairs"
      },
      {
        "slug": "bylaws-rules",
        "label": "Bylaws & building rules"
      },
      {
        "slug": "building-committees",
        "label": "Building committees (vaad bayit)"
      },
      {
        "slug": "mediation-tribunals",
        "label": "Mediation & tribunals"
      }
    ]
  },
  {
    "slug": "identity-privacy",
    "label": "Privacy & Identity Protection",
    "group": "Rights & Benefits",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "identity-theft",
        "label": "Identity theft"
      },
      {
        "slug": "data-breaches",
        "label": "Data breaches"
      },
      {
        "slug": "credit-freeze",
        "label": "Credit freezes & fraud alerts"
      },
      {
        "slug": "online-privacy",
        "label": "Online privacy"
      },
      {
        "slug": "data-requests",
        "label": "Getting or deleting your data"
      },
      {
        "slug": "spam-robocalls",
        "label": "Spam & robocalls"
      },
      {
        "slug": "children-privacy",
        "label": "Children's privacy"
      },
      {
        "slug": "doxxing-harassment",
        "label": "Doxxing & online harassment"
      }
    ]
  },
  {
    "slug": "creators-influencers",
    "label": "Creators & Influencers",
    "group": "Technology & Marketing",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "growing-audience",
        "label": "Growing an audience"
      },
      {
        "slug": "monetization",
        "label": "Monetization"
      },
      {
        "slug": "brand-deals",
        "label": "Brand deals & sponsorships"
      },
      {
        "slug": "creator-contracts",
        "label": "Contracts & content rights"
      },
      {
        "slug": "creator-taxes",
        "label": "Taxes for creators"
      },
      {
        "slug": "platform-policies",
        "label": "Platform policies & strikes"
      },
      {
        "slug": "ai-content",
        "label": "AI tools & AI content"
      },
      {
        "slug": "online-safety",
        "label": "Online harassment & safety"
      }
    ]
  },
  {
    "slug": "grants-funding",
    "label": "Grants & Funding",
    "group": "Business & Career",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "business-grants",
        "label": "Small business grants"
      },
      {
        "slug": "startup-programs",
        "label": "Startup funding programs"
      },
      {
        "slug": "nonprofit-grants",
        "label": "Nonprofit grants"
      },
      {
        "slug": "research-grants",
        "label": "Research grants"
      },
      {
        "slug": "arts-grants",
        "label": "Arts & culture grants"
      },
      {
        "slug": "individual-grants",
        "label": "Grants for individuals"
      },
      {
        "slug": "writing-applications",
        "label": "Writing a strong application"
      },
      {
        "slug": "reporting-compliance",
        "label": "Reporting & compliance"
      }
    ]
  },
  {
    "slug": "startups-fundraising",
    "label": "Startups & Fundraising",
    "group": "Business & Career",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "idea-validation",
        "label": "Idea & market validation"
      },
      {
        "slug": "business-model",
        "label": "Business model & unit economics"
      },
      {
        "slug": "pitch-deck",
        "label": "Pitch deck & storytelling"
      },
      {
        "slug": "pre-seed-seed",
        "label": "Pre-seed & seed rounds"
      },
      {
        "slug": "venture-capital",
        "label": "Venture capital & Series A"
      },
      {
        "slug": "valuation-cap-table",
        "label": "Valuation, dilution & cap table"
      },
      {
        "slug": "non-dilutive",
        "label": "Grants & innovation programs for startups"
      },
      {
        "slug": "accelerators",
        "label": "Accelerators & incubators"
      },
      {
        "slug": "cofounders-equity",
        "label": "Co-founders & equity split"
      },
      {
        "slug": "go-to-market",
        "label": "Go-to-market & first customers"
      }
    ]
  },
  {
    "slug": "study-abroad-admissions",
    "label": "University Admissions & Study Abroad",
    "group": "Education & Coaching",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "choosing-programs",
        "label": "Choosing programs & universities"
      },
      {
        "slug": "applications-essays",
        "label": "Applications & essays"
      },
      {
        "slug": "undergrad-admissions",
        "label": "Undergraduate admissions"
      },
      {
        "slug": "graduate-admissions",
        "label": "Master's & PhD admissions"
      },
      {
        "slug": "study-costs",
        "label": "Tuition, costs & budgeting"
      },
      {
        "slug": "scholarships",
        "label": "Scholarships & financial aid"
      },
      {
        "slug": "credentials-transcripts",
        "label": "Transcripts & credential evaluation"
      },
      {
        "slug": "student-life",
        "label": "Arriving & student life"
      }
    ]
  },
  {
    "slug": "exam-prep",
    "label": "Exam & Test Preparation",
    "group": "Education & Coaching",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "english-tests",
        "label": "IELTS, TOEFL & PTE"
      },
      {
        "slug": "celpip-tef",
        "label": "CELPIP & TEF/TCF"
      },
      {
        "slug": "sat-act",
        "label": "SAT & ACT"
      },
      {
        "slug": "gmat-gre",
        "label": "GMAT & GRE"
      },
      {
        "slug": "lsat-mcat",
        "label": "LSAT & MCAT"
      },
      {
        "slug": "psychometric-bagrut",
        "label": "Psychometric & Bagrut"
      },
      {
        "slug": "study-plan",
        "label": "Study plan & score goals"
      },
      {
        "slug": "test-day",
        "label": "Registration & test day"
      }
    ]
  },
  {
    "slug": "ai-automation-business",
    "label": "AI & Automation for Business",
    "group": "Technology & Marketing",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "use-cases",
        "label": "Where AI helps my business"
      },
      {
        "slug": "tool-selection",
        "label": "Choosing AI tools"
      },
      {
        "slug": "workflow-automation",
        "label": "Workflow automation"
      },
      {
        "slug": "chatbots-support",
        "label": "Chatbots & customer support"
      },
      {
        "slug": "ai-marketing-content",
        "label": "AI for marketing & content"
      },
      {
        "slug": "data-reporting",
        "label": "Data, reports & dashboards"
      },
      {
        "slug": "ai-policy-risk",
        "label": "AI policy, privacy & risks"
      },
      {
        "slug": "roi-rollout",
        "label": "Costs, ROI & rollout"
      }
    ]
  },
  {
    "slug": "weddings-celebrations",
    "label": "Weddings & Celebrations",
    "group": "Lifestyle & Services",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "wedding-budget",
        "label": "Wedding budget"
      },
      {
        "slug": "venues-vendors",
        "label": "Venues & vendors"
      },
      {
        "slug": "timeline-checklist",
        "label": "Timeline & checklist"
      },
      {
        "slug": "guests-invitations",
        "label": "Guests & invitations"
      },
      {
        "slug": "ceremony-traditions",
        "label": "Ceremony & traditions"
      },
      {
        "slug": "destination-wedding",
        "label": "Destination weddings"
      },
      {
        "slug": "bar-bat-mitzvah",
        "label": "Bar & bat mitzvah"
      },
      {
        "slug": "milestone-parties",
        "label": "Anniversaries & milestone parties"
      }
    ]
  },
  {
    "slug": "restaurant-food-service",
    "label": "Restaurants, Cafés & Food Trucks",
    "group": "Small Business by Industry",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "restaurant-opening-costs",
        "label": "Opening costs & budget"
      },
      {
        "slug": "restaurant-location-lease",
        "label": "Location & lease"
      },
      {
        "slug": "food-safety-permits",
        "label": "Permits & food-safety licence"
      },
      {
        "slug": "restaurant-menu-costing",
        "label": "Menu costing & food cost"
      },
      {
        "slug": "restaurant-staffing-tips",
        "label": "Staffing & tips"
      },
      {
        "slug": "food-delivery-apps",
        "label": "Delivery apps & takeout"
      },
      {
        "slug": "restaurant-liquor-licence",
        "label": "Liquor licence"
      },
      {
        "slug": "restaurant-catering-addon",
        "label": "Catering add-on"
      },
      {
        "slug": "food-truck-mobile",
        "label": "Food trucks & mobile vending"
      }
    ]
  },
  {
    "slug": "short-term-rentals",
    "label": "Short-Term Rentals & Vacation Hosting",
    "group": "Property & Real Estate",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "str-rules-permits",
        "label": "Local rules, permits & registration"
      },
      {
        "slug": "str-listing-platforms",
        "label": "Listings on Airbnb, Booking.com & Vrbo"
      },
      {
        "slug": "str-pricing-occupancy",
        "label": "Pricing & occupancy"
      },
      {
        "slug": "str-cleaning-turnover",
        "label": "Cleaning, turnover & check-in"
      },
      {
        "slug": "str-co-hosting-management",
        "label": "Co-hosts & property managers"
      },
      {
        "slug": "str-rental-income-tax",
        "label": "Tax on rental income (info)"
      },
      {
        "slug": "str-guest-problems",
        "label": "Guest problems, damage & reviews"
      },
      {
        "slug": "str-insurance-safety",
        "label": "Insurance & guest safety"
      }
    ]
  },
  {
    "slug": "small-business-buying-selling",
    "label": "Buying or Selling a Small Business",
    "group": "Business & Career",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "business-valuation-basics",
        "label": "Valuing a small business"
      },
      {
        "slug": "business-deal-sourcing-brokers",
        "label": "Finding a business to buy & brokers"
      },
      {
        "slug": "business-purchase-deal-structure",
        "label": "Offers, LOI & deal structure"
      },
      {
        "slug": "business-acquisition-due-diligence",
        "label": "Due diligence"
      },
      {
        "slug": "business-acquisition-financing",
        "label": "Financing a purchase (bank, government-backed, seller notes)"
      },
      {
        "slug": "business-ownership-transition",
        "label": "Handover & transition"
      },
      {
        "slug": "preparing-business-for-sale",
        "label": "Preparing your business for sale"
      },
      {
        "slug": "selling-small-business-process",
        "label": "Selling: brokers, buyers & closing"
      }
    ]
  },
  {
    "slug": "construction-contractor-business",
    "label": "Running a Contracting or Trades Business",
    "group": "Small Business by Industry",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "contractor-licensing-bonding",
        "label": "Contractor licensing & bonding"
      },
      {
        "slug": "construction-estimating-bidding",
        "label": "Estimating & bidding"
      },
      {
        "slug": "construction-job-pricing",
        "label": "Pricing jobs & cash flow"
      },
      {
        "slug": "construction-crews-subcontractors",
        "label": "Crews & subcontractors"
      },
      {
        "slug": "contractor-insurance-workers-comp",
        "label": "Insurance & workers' compensation"
      },
      {
        "slug": "jobsite-safety-compliance",
        "label": "Jobsite safety & injury reporting"
      },
      {
        "slug": "construction-contracts-change-orders",
        "label": "Contracts & change orders"
      },
      {
        "slug": "construction-payment-liens",
        "label": "Getting paid & liens (info)"
      }
    ]
  },
  {
    "slug": "online-courses-coaching-business",
    "label": "Online Courses, Memberships & Coaching Businesses",
    "group": "Business & Career",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "course-niche-offer",
        "label": "Niche & offer"
      },
      {
        "slug": "course-coaching-pricing",
        "label": "Pricing & payment plans"
      },
      {
        "slug": "course-platforms-tech",
        "label": "Course & community platforms"
      },
      {
        "slug": "course-launches-email",
        "label": "Launches & email list"
      },
      {
        "slug": "cohorts-communities-memberships",
        "label": "Cohorts, communities & memberships"
      },
      {
        "slug": "coaching-sales-calls",
        "label": "Sales calls & enrolment"
      },
      {
        "slug": "course-refunds-terms",
        "label": "Refunds, terms & policies"
      },
      {
        "slug": "course-testimonials-claims",
        "label": "Testimonials & income claims"
      }
    ]
  },
  {
    "slug": "home-services-business",
    "label": "Cleaning, Landscaping, Pest & Handyman Businesses",
    "group": "Small Business by Industry",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "home-services-launch",
        "label": "Starting a cleaning, lawn, pest or handyman business"
      },
      {
        "slug": "home-services-licensing",
        "label": "Business licences, trade limits & registration"
      },
      {
        "slug": "pest-control-licensing",
        "label": "Pest control & pesticide licensing"
      },
      {
        "slug": "home-services-insurance",
        "label": "Liability insurance, bonding & workers' compensation"
      },
      {
        "slug": "home-services-pricing",
        "label": "Pricing per job & raising prices"
      },
      {
        "slug": "home-services-marketing",
        "label": "Getting clients, reviews & referrals"
      },
      {
        "slug": "home-services-hiring",
        "label": "Hiring & keeping cleaners and crews"
      },
      {
        "slug": "home-services-recurring-work",
        "label": "Recurring service plans & commercial contracts"
      },
      {
        "slug": "home-services-seasonal",
        "label": "Slow season, snow removal & cash flow"
      }
    ]
  },
  {
    "slug": "beauty-salon-spa-business",
    "label": "Salons, Barbershops & Spa Businesses",
    "group": "Small Business by Industry",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "salon-opening-startup",
        "label": "Opening a salon, barbershop or spa"
      },
      {
        "slug": "salon-chair-booth-rental",
        "label": "Chair & booth rental vs employees"
      },
      {
        "slug": "salon-staff-pay-tips",
        "label": "Staff pay, commission & tips"
      },
      {
        "slug": "salon-licensing-sanitation",
        "label": "Salon licences, inspections & sanitation"
      },
      {
        "slug": "salon-service-pricing",
        "label": "Pricing services & the service menu"
      },
      {
        "slug": "salon-booking-no-shows",
        "label": "Booking software, deposits & no-shows"
      },
      {
        "slug": "salon-retail-products",
        "label": "Retail products & backbar"
      },
      {
        "slug": "salon-chemical-safety",
        "label": "Chemical & client safety"
      },
      {
        "slug": "day-spa-services",
        "label": "Day spa services & treatment rooms"
      },
      {
        "slug": "salon-client-retention",
        "label": "Rebooking, retention & reviews"
      }
    ]
  },
  {
    "slug": "trucking-freight",
    "label": "Trucking, Owner-Operators & Freight",
    "group": "Small Business by Industry",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "trucking-operating-authority",
        "label": "Operating authority & carrier registrations"
      },
      {
        "slug": "truck-licence-classes",
        "label": "Commercial licence classes & driver training"
      },
      {
        "slug": "trucking-fuel-tax-ifta",
        "label": "Fuel tax reporting (IFTA) & road-use taxes"
      },
      {
        "slug": "trucking-lease-on-vs-authority",
        "label": "Leasing on vs your own authority"
      },
      {
        "slug": "freight-loads-brokers",
        "label": "Loads, brokers & getting paid"
      },
      {
        "slug": "truck-financing-costs",
        "label": "Truck financing & operating costs"
      },
      {
        "slug": "trucking-safety-compliance",
        "label": "Hours of service, ELDs & safety audits"
      },
      {
        "slug": "freight-brokerage-dispatch",
        "label": "Starting a freight brokerage or dispatch service"
      }
    ]
  },
  {
    "slug": "import-export-customs",
    "label": "Import, Export & Customs",
    "group": "Business & Career",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "hs-tariff-classification",
        "label": "Tariff classification (HS codes)"
      },
      {
        "slug": "customs-duty-trade-agreements",
        "label": "Duties, origin & trade agreements"
      },
      {
        "slug": "import-landed-cost",
        "label": "Landed cost & new tariffs"
      },
      {
        "slug": "customs-broker-clearance",
        "label": "Customs brokers & clearance"
      },
      {
        "slug": "incoterms-trade-terms",
        "label": "Incoterms & trade terms"
      },
      {
        "slug": "regulated-goods-import-permits",
        "label": "Regulated goods & import permits"
      },
      {
        "slug": "exporting-first-markets",
        "label": "Exporting: documents & first markets"
      },
      {
        "slug": "export-controls-sanctions",
        "label": "Export controls & sanctions (info)"
      },
      {
        "slug": "customs-compliance-corrections",
        "label": "Customs mistakes, audits & disputes (info)"
      }
    ]
  },
  {
    "slug": "product-development-manufacturing",
    "label": "Product Development & Manufacturing",
    "group": "Business & Career",
    "subLabel": "Topic",
    "subMulti": true,
    "subRequired": true,
    "subcategories": [
      {
        "slug": "product-prototyping",
        "label": "Prototyping & design for manufacture"
      },
      {
        "slug": "finding-manufacturers",
        "label": "Finding & vetting manufacturers"
      },
      {
        "slug": "moq-unit-cost",
        "label": "Quotes, MOQs & unit cost"
      },
      {
        "slug": "manufacturer-agreements-tooling",
        "label": "Manufacturer agreements & tooling"
      },
      {
        "slug": "product-safety-certification",
        "label": "Product safety rules & certification"
      },
      {
        "slug": "manufacturing-quality-control",
        "label": "Quality control & inspections"
      },
      {
        "slug": "product-packaging-labelling",
        "label": "Packaging & labelling"
      },
      {
        "slug": "product-recalls-defects",
        "label": "Safety complaints, defects & recalls"
      },
      {
        "slug": "production-scale-up",
        "label": "Scaling up production"
      }
    ]
  },
  {
    "slug": "other",
    "label": "Other / General Advisory",
    "group": "Specialized & Other",
    "subLabel": "Specialization",
    "subMulti": true,
    "subRequired": false,
    "subcategories": [
      {
        "slug": "general-advisory",
        "label": "General advisory"
      }
    ]
  }
];

/** Fetch the authoritative taxonomy from the server; fall back to FALLBACK_TAXONOMY. */
export async function fetchTaxonomy() {
  try {
    const base = process.env.REACT_APP_SERVER || '';
    const res = await fetch(`${base}onboarding/taxonomy`);
    if (!res.ok) throw new Error(`taxonomy ${res.status}`);
    const data = await res.json();
    if (Array.isArray(data?.categories) && data.categories.length) return data.categories;
    throw new Error('empty taxonomy');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[taxonomy] using fallback:', e?.message || e);
    return FALLBACK_TAXONOMY;
  }
}

const norm = (s) => (s || '').toString().trim().toLowerCase();

/** Find a category by slug or (tolerantly) by a stored label / free-text value. */
export function findCategory(taxonomy, categoryOrLabel) {
  const c = norm(categoryOrLabel);
  if (!c || !Array.isArray(taxonomy)) return undefined;
  const exact = taxonomy.find((cat) => cat.slug === c || norm(cat.label) === c);
  if (exact) return exact;
  return taxonomy.find((cat) => c.includes(cat.slug) || c.includes(norm(cat.label)));
}

/** Group categories by their `group` header, preserving first-seen order. */
export function groupCategories(taxonomy) {
  const groups = [];
  const byName = new Map();
  (taxonomy || []).forEach((cat) => {
    if (!byName.has(cat.group)) {
      const entry = { group: cat.group, categories: [] };
      byName.set(cat.group, entry);
      groups.push(entry);
    }
    byName.get(cat.group).categories.push(cat);
  });
  return groups;
}

/** Map a subcategory slug -> its display label within a category. */
export function subcategoryLabel(category, slug) {
  const s = norm(slug);
  const found = category?.subcategories?.find((x) => norm(x.slug) === s);
  return found ? found.label : slug;
}

/** Keep only slugs that are valid for the given category (accepts slugs or labels). */
export function sanitizeSubcategories(category, incoming) {
  if (!category) return [];
  const bySlug = new Map(category.subcategories.map((s) => [norm(s.slug), s.slug]));
  const byLabel = new Map(category.subcategories.map((s) => [norm(s.label), s.slug]));
  const arr = Array.isArray(incoming) ? incoming : incoming != null ? [incoming] : [];
  const out = [];
  arr.forEach((raw) => {
    const slug = bySlug.get(norm(raw)) || byLabel.get(norm(raw));
    if (slug && !out.includes(slug)) out.push(slug);
  });
  return out;
}
